/**
 * Chart bar merge helpers — keep CT open-aligned and end-stamped copies from forking candles.
 * Yahoo's forming bar is stamped at "now"; snapBars folds that back onto the candle open.
 */

import { isDailyUnit, isMonthlyUnit, isWeeklyUnit, snapBarsToPeriod, snapDailyBars, snapMonthlyBars, snapWeeklyBars } from '../lib/yahooFinance/snapBars';

function barEpochMs(bar) {
  if (!bar) return NaN;
  if (bar.Epoch != null && Number.isFinite(Number(bar.Epoch))) return Number(bar.Epoch);
  if (bar.TimeStamp) {
    const ms = new Date(bar.TimeStamp).getTime();
    return Number.isFinite(ms) ? ms : NaN;
  }
  return NaN;
}

function barMergeKey(bar) {
  const ms = barEpochMs(bar);
  if (Number.isFinite(ms)) return `e:${ms}`;
  return null;
}

function sameBarOhlcv(a, b) {
  if (!a || !b) return false;
  return (
    String(a.Open) === String(b.Open) &&
    String(a.High) === String(b.High) &&
    String(a.Low) === String(b.Low) &&
    String(a.Close) === String(b.Close) &&
    String(a.TotalVolume ?? a.Volume ?? '') === String(b.TotalVolume ?? b.Volume ?? '')
  );
}

/** Match server tsIntervalToCt + period ms for intraday chart coalescing. */
export function chartIntervalToPeriodMs(interval, unit) {
  const n = parseInt(interval, 10) || 1;
  const u = String(unit || 'Minute').toLowerCase();
  if (u.startsWith('min')) return n * 60 * 1000;
  if (u.startsWith('hour') || u === 'hourly') return n * 60 * 60 * 1000;
  return null;
}

/** Daily/weekly opens are not epoch-aligned; still one bucket per candle. */
function calendarPeriodMs(interval, unit) {
  const n = parseInt(interval, 10) || 1;
  const u = String(unit || '').toLowerCase();
  // Match the app's unit names exactly. startsWith('day') is not reliable here:
  // in this Babel pipeline it does not match "daily".
  if (u === 'daily' || u === 'day') return n * 24 * 60 * 60 * 1000;
  if (u === 'weekly' || u === 'week') return n * 7 * 24 * 60 * 60 * 1000;
  return null;
}

function pickReplacementBar(existing, incoming) {
  if (!existing) return incoming;
  if (!incoming) return existing;
  // Never blend candle fields. Keep closed bars sticky when stale open updates arrive.
  if (existing.BarStatus === 'Closed' && incoming.BarStatus !== 'Closed') return existing;
  return incoming;
}

/**
 * Drop the later of two adjacent identical-OHLCV bars spaced exactly one period apart
 * (CT closed bar stamped at exclusive end next to open-aligned copy).
 */
export function coalesceEndStampedDuplicateBars(bars, periodMs) {
  if (!Array.isArray(bars) || bars.length < 2 || !periodMs) return bars;
  const sorted = [...bars].sort((a, b) => barEpochMs(a) - barEpochMs(b));
  const out = [];
  for (const bar of sorted) {
    const prev = out[out.length - 1];
    const prevEpoch = barEpochMs(prev);
    const epoch = barEpochMs(bar);
    if (
      prev &&
      Number.isFinite(prevEpoch) &&
      Number.isFinite(epoch) &&
      epoch > prevEpoch &&
      epoch - prevEpoch <= periodMs &&
      sameBarOhlcv(prev, bar)
    ) {
      if (bar.BarStatus === 'Closed') prev.BarStatus = 'Closed';
      continue;
    }
    out.push(bar);
  }
  return out;
}

export function mergeBars(existingBars, incomingBars, { interval, unit } = {}) {
  const intradayMs = chartIntervalToPeriodMs(interval, unit);
  const snapMs = intradayMs || calendarPeriodMs(interval, unit);
  const combined = [];
  if (Array.isArray(existingBars)) combined.push(...existingBars);
  if (Array.isArray(incomingBars)) combined.push(...incomingBars);

  let merged;
  if (isMonthlyUnit(unit)) {
    merged = snapMonthlyBars(combined);
  } else if (isDailyUnit(unit)) {
    merged = snapDailyBars(combined);
  } else if (isWeeklyUnit(unit) && (parseInt(interval, 10) || 1) === 1) {
    merged = snapWeeklyBars(combined);
  } else if (snapMs) {
    merged = snapBarsToPeriod(combined, snapMs);
  } else {
    const byKey = new Map();
    for (const bar of combined) {
      const key = barMergeKey(bar);
      if (!key) continue;
      byKey.set(key, pickReplacementBar(byKey.get(key), bar));
    }
    merged = Array.from(byKey.values()).sort((a, b) => barEpochMs(a) - barEpochMs(b));
  }
  return coalesceEndStampedDuplicateBars(merged, intradayMs);
}
